import ky from 'ky';
import { useAuthStore } from '@/stores/auth';

const baseUrl = import.meta.env.VITE_API_BASE_URL ?? '';

function tryJsonParse(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function cleanMessage(value: string): string {
  return value.trim();
}

export function extractApiMessage(input: unknown, fallback = '请重试'): string {
  if (!input) return fallback;
  if (typeof input === 'string') {
    const text = input.trim();
    if (!text) return fallback;

    const nested = tryJsonParse(text);
    if (nested) return extractApiMessage(nested, fallback);

    const jsonStart = text.indexOf('{');
    if (jsonStart >= 0) {
      const nestedFromTail = tryJsonParse(text.slice(jsonStart));
      if (nestedFromTail) return extractApiMessage(nestedFromTail, fallback);
    }

    const messageMatch = text.match(/"message"\s*:\s*"((?:\\.|[^"\\])*)"/);
    if (messageMatch?.[1]) return cleanMessage(messageMatch[1].replace(/\\"/g, '"'));

    return cleanMessage(text);
  }
  if (input instanceof Error) {
    const maybeResponse = (input as Error & { response?: Response }).response;
    if (maybeResponse) return input.message || fallback;
    return extractApiMessage(input.message, fallback);
  }
  if (typeof input === 'object') {
    const obj = input as Record<string, unknown>;
    if (obj.error) return extractApiMessage(obj.error, fallback);
    if (obj.message) return extractApiMessage(obj.message, fallback);
    if (obj.detail) return extractApiMessage(obj.detail, fallback);
  }
  return fallback;
}

export const api = ky.create({
  prefixUrl: `${baseUrl}/api/v1`,
  timeout: 180_000,
  hooks: {
    beforeRequest: [
      (req) => {
        const token = useAuthStore.getState().token;
        if (token) req.headers.set('Authorization', `Bearer ${token}`);
      },
    ],
    afterResponse: [
      async (_req, _opts, res) => {
        if (res.status === 401) {
          useAuthStore.getState().clear();
          if (!window.location.pathname.startsWith('/login')) {
            window.location.href = '/login';
          }
        }
        return res;
      },
    ],
    beforeError: [
      async (error) => {
        try {
          const body = await error.response.clone().json();
          error.message = extractApiMessage(body, error.response.statusText);
        } catch {
          try {
            const body = await error.response.clone().text();
            error.message = extractApiMessage(body, error.response.statusText);
          } catch {
            error.message = error.response.statusText || error.message;
          }
        }
        return error;
      },
    ],
  },
});

/** SSE 流式订阅 (用于 LLM token / Atlas 增量) */
export function streamSse<T>(
  path: string,
  init: RequestInit,
  onEvent: (data: T) => void,
  onError?: (err: unknown) => void,
  onClose?: () => void,
): AbortController {
  const ctrl = new AbortController();
  const url = `${baseUrl}/api/v1/${path}`;
  const token = useAuthStore.getState().token;
  const headers = new Headers(init.headers);
  if (token && !headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`);
  fetch(url, { ...init, headers, signal: ctrl.signal })
    .then(async (res) => {
      if (!res.ok) {
        let detail = '';
        try {
          detail = await res.text();
        } catch {
          /* ignore */
        }
        onError?.(new Error(`HTTP ${res.status}: ${detail.slice(0, 300) || res.statusText}`));
        return;
      }
      if (!res.body) return;
      const reader = res.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buffer.indexOf('\n\n')) >= 0) {
          const chunk = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          const line = chunk.split('\n').find((l) => l.startsWith('data:'));
          if (!line) continue;
          try {
            onEvent(JSON.parse(line.slice(5).trim()));
          } catch (e) {
            onError?.(e);
          }
        }
      }
      onClose?.();
    })
    .catch((e) => {
      if ((e as Error)?.name !== 'AbortError') onError?.(e);
      onClose?.();
    });
  return ctrl;
}

/** WebSocket 包装(抽卡候选流、协作 Yjs) */
export function openWs(path: string, onMessage: (msg: unknown) => void): WebSocket {
  const wsBase = import.meta.env.VITE_WS_BASE_URL || baseUrl.replace(/^http/, 'ws');
  const ws = new WebSocket(`${wsBase}/api/v1/${path}`);
  ws.onmessage = (e) => {
    try {
      onMessage(JSON.parse(e.data));
    } catch {
      /* binary frame */
    }
  };
  return ws;
}
